import { sortAsc } from '@strata/helper-functions';
import { DateInputFormik } from '@strata/react-date-time';
import { DialogWithForm } from '@strata/react-dialog';
import { SuggestMultiSelectFormik, SuggestSingleSelectFormik } from '@strata/react-selects';
import { validationStrings } from '@strata/strata-ui-strings';
import dayjs from 'dayjs';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import * as Yup from 'yup';

import { handleError } from 'services/ErrorManagement';
import { post, useGet } from 'services/Http';

export const CreateDuplicatePolicyDialog = ({
    isOpen,
    onSecondaryButtonClick,
    payerId = undefined,
    sourceDraftId = undefined,
}) => {
    const navigate = useNavigate();

    const medications = useGet(
        isOpen ? '/reference/productservices/summaryonly?intake_only=true' : null,
    );
    const medicationsList = useMemo(
        () =>
            medications
                ?.map((med) => ({
                    name: med.description,
                    secondaryData: med.oid,
                }))
                .sort((a, b) => sortAsc(a.name, b.name)),
        [medications],
    );

    const diagnoses = useGet(isOpen ? '/reference/diagnoses/' : null);
    const diagnosesList = useMemo(
        () =>
            diagnoses
                ?.map((dx) => ({
                    name: dx.description,
                    secondaryData: dx.oid,
                }))
                .sort((a, b) => sortAsc(a.name, b.name)),
        [diagnoses],
    );

    return (
        <Formik
            initialValues={{
                diagnoses: [],
            }}
            enableReinitialize
            validationSchema={Yup.object({
                product: Yup.object().required(validationStrings.requiredField.errorMessage),
                diagnoses: Yup.array().min(1, validationStrings.requiredField.errorMessage),
                effectiveDate: DateInputFormik.validator().required(
                    validationStrings.requiredField.errorMessage,
                ),
            })}
            onSubmit={async (values, { resetForm }) => {
                try {
                    const response = await post(
                        `/clinicalpolicy/admin/${payerId}/drafts${sourceDraftId ? '/clone' : ''}`,
                        {
                            product_oid: values.product.secondaryData,
                            product_name: values.product.name,
                            diagnosis_oids: values.diagnoses
                                .map((dx) => dx.secondaryData)
                                .sort(sortAsc),
                            effective_date: dayjs(values.effectiveDate).toISOString(),
                            source_draft_id: sourceDraftId,
                        },
                    );
                    resetForm();
                    onSecondaryButtonClick();
                    navigate(`/policy-edit/${payerId}/${response.draft_id}`);
                } catch (err) {
                    handleError(
                        `Error ${sourceDraftId ? 'duplicating' : 'creating'} clinical policy.`,
                        err,
                    );
                }
            }}
        >
            {(formik) => (
                <DialogWithForm
                    dialogTitle={sourceDraftId ? 'Duplicate Policy' : 'Create Clinical Policy'}
                    primaryButtonLabel="Save"
                    secondaryButtonLabel="Cancel"
                    isOpen={isOpen}
                    onSecondaryButtonClick={() => {
                        formik.handleReset();
                        onSecondaryButtonClick();
                    }}
                    isPrimaryButtonDisabled={
                        !(formik.dirty && formik.isValid) || formik.isSubmitting
                    }
                    dialogContent={
                        <>
                            {sourceDraftId && (
                                <p>
                                    Duplicating this policy will create a new Draft policy with the
                                    same clinical questions and logic.
                                </p>
                            )}
                            <SuggestSingleSelectFormik
                                id="product"
                                label="Product"
                                required
                                isLoading={!medicationsList}
                                values={medicationsList}
                            />
                            <SuggestMultiSelectFormik
                                id="diagnoses"
                                label="Diagnoses"
                                required
                                isLoading={!diagnosesList}
                                values={diagnosesList}
                            />
                            <DateInputFormik
                                id="effectiveDate"
                                label="Effective Date"
                                required
                                showTime
                            />
                        </>
                    }
                />
            )}
        </Formik>
    );
};

CreateDuplicatePolicyDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onSecondaryButtonClick: PropTypes.func.isRequired,
    payerId: PropTypes.string,
    sourceDraftId: PropTypes.string,
};
